import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { R400 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { ApprovalType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ApprovalStatus, useTaskList } from '@css-xp/apd-task-manager';
import { ErrorComponents, NotificationTypes, errorNotificationBuilder, useNotification } from '@css-xp/base';

interface PageProps {
  promotions: number;
  entitlementNumber: string | undefined | null;
}

export const PromotionsSummary = ({ promotions, entitlementNumber }: PageProps) => {
  const { setNotificationSent, sendErrorNotification } = useNotification();
  const { isDiscountApprovalFlowEnabled } = useApprovalDeskPermissions();
  const { loading: taskListLoading, error: taskListError, hasOpenApprovalRequest, approvalStatus } = useTaskList({
    businessKey: entitlementNumber,
    skip: !isDiscountApprovalFlowEnabled || !entitlementNumber,
    approvalType: ApprovalType.ORDER_DISCOUNT,
  });

  useEffect(() => {
    if (taskListError) {
      const notificationOptions = errorNotificationBuilder()
        .withId('trial-extension-request-notification-error')
        .withHeader('Error')
        .withMessage(
          <p>
            An error occurred while contacting approval desk - try again later. Still having trouble? Contact{' '}
            {<ErrorComponents.ChatRoomLink />}
          </p>
        )
        .withDismissAfter(3000)
        .isVersion(NotificationTypes.FLAG)
        .build();
      sendErrorNotification(notificationOptions);
      setNotificationSent(true);
    }
  }, [taskListError]);

  return (
    <div>
      <>
        <label>Discounts </label>
        <Tooltip content="Includes discounts that will be applied to the next bill." tag="span">
          <InfoIcon size="small" label="active-promotion-info" />
        </Tooltip>
      </>
      <div className="entitlement-details-column">
        <div className="promotions-summary">
          <div className="promotions-active-label">Active: </div>
          {promotions && promotions > 0 ? (
            <>
              <div data-testid="active-promotions-value">{promotions}</div>
              <div className="promotions-view-link">
                <Link to={{ hash: '#discounts' }} className="inner-text">
                  View
                </Link>
              </div>
            </>
          ) : (
            <div data-testid="promotions-data-unavailable" className="data-unavailable">
              0
            </div>
          )}
        </div>
        <div className="promotions-footer">
          <div className="promotions-other-label">Pending: </div>
          {taskListLoading && (
            <div>
              <LoadingIndicator size="medium" />
            </div>
          )}
          {taskListError && (
            <div data-testid="promotions-request-error" className="promotions-request-error">
              <div className="error-toast-icon">
                <ErrorIcon key={'error'} label="Error" primaryColor={R400} size="medium" />
              </div>
              <div className="error-text">Something went wrong</div>
            </div>
          )}
          {hasOpenApprovalRequest && (
            <div className="promotions-wrap-text">
              {approvalStatus && (
                <div className="promotions-approval-status">
                  <ApprovalStatus status={approvalStatus} />
                </div>
              )}
              <div>
                <Link to={{ hash: '#approvals' }} className="inner-text">
                  View
                </Link>
              </div>
            </div>
          )}
          {!taskListLoading && !taskListError && !hasOpenApprovalRequest && (
            <div data-testid="promotions-approval-data-unavailable" className="data-unavailable">
              {' '}
              N/A{' '}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
